import React, { useState } from "react";
import {
    Alert,
    Box,
    Chip,
    FormControlLabel,
    Grid,
    Paper,
    Stack,
    Switch,
    TableCell,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import { FileDownloadOutlined as DownloadIcon } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import TableCommon from "../../Common/Table";
import { SearchRecords } from "../../Common/SearchRecords";
import Button from "../../Common/Button";
import { setSnackBar } from "../../../store/common/commonSlice";
import {
    useGetAffiliateDashboardQuery,
    useGetAffiliatesQuery,
    useLazyGetAffiliatesQuery,
} from "../../../store/shopAdmin/shopAdminApis";
import AffiliateDetailsDialog from "./AffiliateDetailsDialog";
import { csvDate, downloadCsv, rupees } from "./affiliateUtils";

const columns = [
    { title: "Affiliate", field: "name", sort: false, width: "22%" },
    { title: "Code", field: "referral_code", sort: false, width: "10%" },
    { title: "Referred", field: "referrals", sort: false, width: "8%", align: "right" },
    { title: "Referral sales", field: "referral_gmv", sort: false, width: "14%", align: "right" },
    { title: "Pending", field: "pending", sort: false, width: "10%", align: "right" },
    { title: "Wallet", field: "balance", sort: false, width: "10%", align: "right" },
    { title: "Checks", field: "flags", sort: false, width: "26%" },
];

const CSV_COLUMNS = [
    ["Name", (r) => r.name],
    ["Mobile", (r) => r.mobile],
    ["Referral code", (r) => r.referral_code],
    ["Referrals blocked", (r) => (r.affiliate_blocked ? "Yes" : "No")],
    ["Referred customers", (r) => r.referrals],
    ["Referral orders", (r) => r.referral_orders],
    ["Referral sales (₹)", (r) => r.referral_gmv],
    ["Pending (₹)", (r) => r.pending],
    ["Wallet balance (₹)", (r) => r.balance],
    ["Earned lifetime (₹)", (r) => r.lifetime_earned],
    ["Checks", (r) => r.flags.map((f) => f.label).join("; ")],
    ["Joined", (r) => csvDate(r.created_at)],
];

const AffiliateOverview = () => {
    const dispatch = useDispatch();
    const [pagination, setPagination] = useState({
        pageNo: 0,
        limit: 10,
        sortBy: "DESC",
        sortField: "referral_gmv",
        keywords: "",
    });
    const [flagged, setFlagged] = useState(false);
    const [openId, setOpenId] = useState(null);
    const { data: dash } = useGetAffiliateDashboardQuery();
    const { data, isFetching, error } = useGetAffiliatesQuery({
        page: pagination.pageNo + 1,
        limit: pagination.limit,
        keywords: pagination.keywords,
        flagged,
    });
    const [fetchAll, { isFetching: exporting }] = useLazyGetAffiliatesQuery();

    const exportCsv = async () => {
        try {
            const all = await fetchAll({
                page: 1,
                limit: 100,
                keywords: pagination.keywords,
                flagged,
                exportAll: true,
            }).unwrap();
            downloadCsv(`magiclock-affiliates-${new Date().toISOString().slice(0, 10)}.csv`, CSV_COLUMNS, all.results);
        } catch (e) {
            dispatch(setSnackBar({ open: true, message: e?.data?.message || "Export failed", severity: "error" }));
        }
    };

    const cards = dash
        ? [
              [
                  "Affiliates",
                  dash.affiliates,
                  "text.primary",
                  `${dash.referred_customers} customer${dash.referred_customers === 1 ? "" : "s"} referred`,
              ],
              ["Active (30 days)", dash.active_referrers_30d, "info.main", "Referrers with a paid order"],
              ["Referral sales", rupees(dash.referral_gmv), "success.main", `${dash.referral_orders} paid orders`],
              ["Pending rewards", rupees(dash.pending), "warning.main", "Waiting for the trigger"],
              [
                  "Credited rewards",
                  rupees(dash.credited),
                  "success.main",
                  `${rupees(dash.wallet_balance)} in wallets now`,
              ],
          ]
        : [];

    const rows =
        !isFetching && data?.results?.length
            ? data.results.map((row) => (
                  <TableRow
                      key={row.id}
                      hover
                      sx={{ "td, th": { border: 0, padding: "14px 12px" }, cursor: "pointer" }}
                      onClick={() => setOpenId(row.id)}
                  >
                      <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                              {row.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                              {row.mobile}
                          </Typography>
                      </TableCell>
                      <TableCell>
                          <Chip size="small" variant="outlined" label={row.referral_code} />
                      </TableCell>
                      <TableCell align="right">{row.referrals}</TableCell>
                      <TableCell align="right">
                          {rupees(row.referral_gmv)}
                          <Typography variant="caption" display="block" color="text.secondary">
                              {row.referral_orders} orders
                          </Typography>
                      </TableCell>
                      <TableCell align="right">{rupees(row.pending)}</TableCell>
                      <TableCell align="right">
                          <strong>{rupees(row.balance)}</strong>
                      </TableCell>
                      <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {row.affiliate_blocked ? (
                                  <Chip size="small" color="error" label="Referrals blocked" />
                              ) : null}
                              {row.flags.map((f) => (
                                  <Tooltip key={f.code} title={f.label}>
                                      <Chip size="small" color="warning" variant="outlined" label={f.label} />
                                  </Tooltip>
                              ))}
                              {!row.flags.length && !row.affiliate_blocked ? (
                                  <Typography variant="caption" color="text.secondary">
                                      No issues
                                  </Typography>
                              ) : null}
                          </Stack>
                      </TableCell>
                  </TableRow>
              ))
            : null;

    return (
        <Stack spacing={2}>
            {dash && !dash.program_enabled ? (
                <Alert severity="warning">
                    The referral program is off. Customers see it as paused and no new rewards are created.
                </Alert>
            ) : null}
            <Grid container spacing={2}>
                {cards.map(([label, value, color, hint]) => (
                    <Grid item xs={12} sm={6} md={2.4} key={label}>
                        <Paper sx={{ p: 2, textAlign: "center", height: "100%" }}>
                            <Typography variant="h5" color={color}>
                                {value}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                                {label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {hint}
                            </Typography>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                alignItems={{ md: "center" }}
                justifyContent="space-between"
            >
                <Typography variant="subtitle1" fontWeight={700}>
                    Affiliates
                    <span style={{ color: "gray", fontSize: "10px", verticalAlign: "super", marginLeft: 8 }}>
                        ({data?.count || 0})
                    </span>
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={flagged}
                                onChange={(e) => {
                                    setFlagged(e.target.checked);
                                    setPagination((p) => ({ ...p, pageNo: 0 }));
                                }}
                            />
                        }
                        label="Only with checks"
                    />
                    <Box sx={{ minWidth: 240 }}>
                        <SearchRecords handlePagination={(p) => setPagination(p)} pagination={pagination} />
                    </Box>
                    <Button
                        fullWidth={false}
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={exportCsv}
                        loading={exporting}
                    >
                        Export CSV
                    </Button>
                </Stack>
            </Stack>
            <Typography variant="caption" color="text.secondary">
                Search by name, mobile or referral code. Click a row for rewards, referred customers, wallet history and
                manual adjustments.
            </Typography>
            {error ? <Alert severity="error">{error?.data?.message || "Failed to load affiliates"}</Alert> : null}

            <TableCommon
                columns={columns}
                tableData={rows}
                count={data?.count || 0}
                loading={isFetching}
                pagination={pagination}
                handlePagination={(p) => setPagination(p)}
                emptyMessage={
                    flagged
                        ? "No affiliates need checking"
                        : "No affiliates yet. A customer shows here once someone joins with their code."
                }
            />

            <AffiliateDetailsDialog affiliateId={openId} onClose={() => setOpenId(null)} />
        </Stack>
    );
};

export default AffiliateOverview;
